/**
 * Iter214 — Sales Command Center (Fase 2 of the PRD).
 *
 * Header + "What Changed?" strip + Action Center tiles + Priority Leads
 * table + Log Call modal glue. Consumed by SmithDashboard as the top
 * section (before the legacy KPI row + tools).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, RefreshCw, Loader2, Sparkles, Flame, PhoneCall, Bell, Mail,
  MoonStar, ChevronRight, Zap, TrendingUp, Building2, Send, Filter,
  Users, ExternalLink, ShieldCheck, CircleDot, ArrowRight, Bookmark,
  BookmarkPlus, X, Trash2, Brain, MessageSquareQuote, Check,
} from "lucide-react";
import LogCallModal from "./LogCallModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FRESHNESS = {
  fresh: { color: "#10B981", label: "Fresh",  hint: "≤ 24h since last sync" },
  aging: { color: "#F59E0B", label: "Aging",  hint: "24-72h since last sync" },
  stale: { color: "#EF4444", label: "Stale",  hint: "> 72h since last sync" },
};

const ACTION_TILES = [
  { id: "priority",       icon: Flame,     color: "#EF4444", label: "Priority",       sub: "Score ≥ 80" },
  { id: "contact_today",  icon: PhoneCall, color: "#0EA5E9", label: "Contact Today",  sub: "Never worked" },
  { id: "followups_due",  icon: Bell,      color: "#F59E0B", label: "Follow-ups Due", sub: "Overdue" },
  { id: "replies",        icon: Mail,      color: "#10B981", label: "Replies",        sub: "Awaiting response" },
  { id: "stale",          icon: MoonStar,  color: "#64748B", label: "Stale Leads",    sub: "No activity 14d+" },
];

const WORKED_STYLE = {
  NOT_WORKED:        { color: "#94A3B8", label: "Not Worked", dot: "⚪" },
  WORKED:            { color: "#10B981", label: "Worked",     dot: "🟢" },
  FOLLOW_UP_REQUIRED:{ color: "#F59E0B", label: "Follow-up",  dot: "🟡" },
  REPLIED:           { color: "#0EA5E9", label: "Replied",    dot: "🔵" },
  MEETING_SCHEDULED: { color: "#8B5CF6", label: "Meeting",    dot: "🟣" },
  QUALIFIED:         { color: "#22C55E", label: "Qualified",  dot: "✅" },
  DISQUALIFIED:      { color: "#EF4444", label: "Disqual.",   dot: "❌" },
};

function timeAgo(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  const diffMin = Math.max(0, (Date.now() - dt.getTime()) / 60000);
  if (diffMin < 1)   return "just now";
  if (diffMin < 60)  return `${Math.round(diffMin)}m ago`;
  if (diffMin < 1440) return `${Math.round(diffMin / 60)}h ago`;
  const days = Math.round(diffMin / 1440);
  if (days < 30) return `${days}d ago`;
  return dt.toLocaleDateString();
}

function absTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export default function SmithCommandCenter() {
  const [lastRun, setLastRun] = useState(null);
  const [freshness, setFreshness] = useState(null);
  const [whatChanged, setWhatChanged] = useState(null);
  const [tiles, setTiles] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("priority");
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  // Iter233 · Sales Intel bulk selection + email-hygiene filters
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [onlyWithEmail, setOnlyWithEmail] = useState(() => {
    try { return localStorage.getItem("smith.cc.onlyWithEmail") !== "0"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("smith.cc.onlyWithEmail", onlyWithEmail ? "1" : "0"); }
    catch { /* quota */ }
  }, [onlyWithEmail]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [logCallProspect, setLogCallProspect] = useState(null);

  // Iter215 · Saved Views + AI Next Best Action
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [savingView, setSavingView] = useState(false);
  const [nbaMap, setNbaMap] = useState({});           // prospect_id → nba
  const [nbaShown, setNbaShown] = useState(false);
  const [nbaLoading, setNbaLoading] = useState(false);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }),
    []
  );

  const loadSummary = async () => {
    try {
      const [lr, wc, ac] = await Promise.all([
        axios.get(`${API}/smith/activity/data-sync/last-run`, authHeader),
        axios.get(`${API}/smith/activity/what-changed?since_hours=168`, authHeader),
        axios.get(`${API}/smith/activity/action-center`, authHeader),
      ]);
      setLastRun(lr.data?.last_run);
      setFreshness(lr.data?.freshness);
      setWhatChanged(wc.data);
      setTiles(ac.data);
    } catch (ex) {
      /* silent — first page-load can happen before backend seeded */
    }
  };

  const loadSavedViews = async () => {
    try {
      const r = await axios.get(`${API}/smith/activity/saved-views`, authHeader);
      setSavedViews(r.data?.views || []);
    } catch { /* silent */ }
  };

  // Iter233 · Sales Intelligence hygiene rules:
  //   1) Only leads with a valid contact email (regra de negócio)
  //   2) Drop duplicates by email (cross-list dedupe)
  const visibleLeads = useMemo(() => {
    let base = leads;
    if (onlyWithEmail) {
      base = base.filter((L) => {
        // Primary DM email OR any contact email in champion_dm chain
        const emails = [
          L.email, L.champion_dm?.email, L.primary_email,
          ...(Array.isArray(L.decision_makers)
            ? L.decision_makers.map((d) => d?.email) : []),
        ].filter(Boolean);
        return emails.some((e) => typeof e === "string" && e.includes("@"));
      });
    }
    const seen = new Set();
    const deduped = [];
    for (const L of base) {
      const key = (L.email || L.champion_dm?.email
                    || L.primary_email || `id:${L.id}`).toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(L);
    }
    return deduped;
  }, [leads, onlyWithEmail]);

  const hiddenCount = leads.length - visibleLeads.length;

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === visibleLeads.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(visibleLeads.map((L) => L.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!confirm(`Excluir ${ids.length} lead(s) selecionado(s)? Somente leads sem histórico (New Prospect / Researching / Junk) serão removidos.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/prospects/kanban-bulk-delete`,
        { prospect_ids: ids }, authHeader);
      const deleted = data?.deleted ?? 0;
      const blocked = data?.blocked ?? 0;
      if (deleted > 0 && blocked > 0) {
        toast.success(`${deleted} excluído(s) · ${blocked} bloqueado(s) por histórico`);
      } else if (deleted > 0) {
        toast.success(`${deleted} lead(s) excluído(s)`);
      } else if (blocked > 0) {
        toast.warning(`Nenhum excluído — ${blocked} lead(s) já têm histórico (Contacted+)`);
      } else {
        toast.info("Nada para excluir");
      }
      setSelectedIds(new Set());
      loadLeads(filter);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha na exclusão em lote");
    } finally { setBulkDeleting(false); }
  };

  const loadLeads = async (f = filter) => {
    setLeadsLoading(true);
    try {
      const r = await axios.get(
        `${API}/smith/activity/priority-leads?filter=${f}&limit=50&since_hours=168`,
        authHeader);
      setLeads(r.data?.items || []);
      // Reset NBA map when the row-set changes — cache still lives server-side.
      setNbaMap({});
      if (nbaShown && r.data?.items?.length) {
        // Auto-refresh AI cells for the new rows.
        fetchNBAs(r.data.items.map((x) => x.id));
      }
    } catch (ex) {
      toast.error("Falha ao carregar leads");
    } finally { setLeadsLoading(false); }
  };

  const fetchNBAs = async (ids) => {
    if (!ids?.length) return;
    setNbaLoading(true);
    try {
      const r = await axios.post(
        `${API}/smith/activity/next-best-actions/bulk`,
        { prospect_ids: ids },
        { ...authHeader, timeout: 60000 });
      setNbaMap((prev) => ({ ...prev, ...(r.data?.results || {}) }));
    } catch (ex) {
      toast.error("Claire falhou ao sugerir");
    } finally { setNbaLoading(false); }
  };

  const toggleNBA = async () => {
    const next = !nbaShown;
    setNbaShown(next);
    if (next && leads.length) {
      await fetchNBAs(leads.map((l) => l.id));
    }
  };

  const applyNBA = async (pid) => {
    try {
      await axios.post(
        `${API}/smith/activity/prospects/${pid}/apply-next-best-action`,
        {}, authHeader);
      toast.success("Next Action aplicado ao lead");
      await loadLeads(filter);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao aplicar");
    }
  };

  useEffect(() => {
    loadSummary();
    loadSavedViews();
    loadLeads(filter);
    /* eslint-disable-next-line */
  }, []);
  useEffect(() => { loadLeads(filter); /* eslint-disable-next-line */ }, [filter]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/smith/activity/data-sync/refresh`,
        {}, authHeader);
      const d = r.data?.delta || {};
      toast.success(
        `Sync completo · ${d.new_leads || 0} novos · ${d.updated_leads || 0} atualizados`);
      setLastRun(r.data?.last_run);
      setFreshness(r.data?.freshness);
      await loadSummary();
      await loadLeads(filter);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha no refresh");
    } finally { setRefreshing(false); }
  };

  const markContacted = async (prospectId, channel = "PHONE_CALL") => {
    try {
      await axios.post(
        `${API}/smith/activity/prospects/${prospectId}/mark-contacted`,
        { channel, notes: "Marked contacted via quick action" },
        authHeader);
      toast.success("Marcado como contactado");
      await loadLeads(filter);
      await loadSummary();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao marcar");
    }
  };

  const applyView = (view) => {
    setActiveViewId(view.id);
    setFilter(view.config?.filter || "priority");
  };

  const saveCurrentView = async (name) => {
    if (!name?.trim()) return;
    setSavingView(true);
    try {
      const r = await axios.post(`${API}/smith/activity/saved-views`,
        { name: name.trim(), icon: "★",
          config: { filter, since_hours: 168 } }, authHeader);
      toast.success(`View "${r.data?.view?.name}" salva`);
      await loadSavedViews();
      setActiveViewId(r.data?.view?.id);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao salvar view");
    } finally { setSavingView(false); }
  };

  const deleteView = async (view) => {
    if (view.is_default) {
      toast.info("Views padrão não podem ser deletadas");
      return;
    }
    if (!window.confirm(`Remover view "${view.name}"?`)) return;
    try {
      await axios.delete(`${API}/smith/activity/saved-views/${view.id}`, authHeader);
      toast.success("View removida");
      if (activeViewId === view.id) setActiveViewId(null);
      await loadSavedViews();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao remover");
    }
  };

  const fresh = FRESHNESS[freshness?.status] || FRESHNESS.stale;

  return (
    <div className="space-y-4" data-testid="smith-command-center">

      {/* ── Sales Intelligence banner ─────────────────────────────── */}
      <section data-testid="smith-cc-header"
        className="rounded-2xl border border-white/10 p-4"
        style={{ background: "linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(11,17,32,0.6) 100%)" }}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#0EA5E9 0%,#0369A1 100%)" }}>
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-sky-300 font-bold">
                Smith Protective · Sales Intelligence
              </div>
              <div className="flex items-center gap-2 text-[12px] text-slate-300">
                <span data-testid="smith-cc-last-updated">
                  Last Updated: <span className="font-bold text-white">
                    {lastRun?.completed_at ? absTime(lastRun.completed_at) : "never"}
                  </span>
                </span>
                <span className="text-slate-600">·</span>
                <span className="inline-flex items-center gap-1 font-bold"
                      title={fresh.hint}
                      data-testid="smith-cc-freshness-dot"
                      style={{ color: fresh.color }}>
                  <CircleDot className="w-3 h-3" fill={fresh.color} /> {fresh.label}
                </span>
              </div>
            </div>
          </div>
          <button type="button" onClick={refresh} disabled={refreshing}
            data-testid="smith-cc-refresh-btn"
            className="h-9 px-3 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#0EA5E9 0%,#0369A1 100%)" }}>
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {refreshing ? "Syncing…" : "Refresh Data"}
          </button>
        </div>
      </section>

      {/* ── What Changed strip ──────────────────────────────────── */}
      <section data-testid="smith-cc-what-changed"
        className="rounded-2xl border border-emerald-500/20 p-3"
        style={{ background: "rgba(16,185,129,0.06)" }}>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
          <h3 className="text-[11px] uppercase tracking-widest text-emerald-200 font-bold">
            What Changed Since Your Last Visit
          </h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <Chip icon={TrendingUp} color="#10B981" label="New Leads"
            value={whatChanged?.new_leads ?? 0}
            onClick={() => setFilter("new")}
            testid="smith-cc-chip-new-leads" />
          <Chip icon={Sparkles} color="#8B5CF6" label="Leads Enriched"
            value={whatChanged?.updated_leads ?? 0}
            testid="smith-cc-chip-updated" />
          <Chip icon={Mail} color="#0EA5E9" label="New Emails Found"
            value={whatChanged?.new_emails ?? 0}
            testid="smith-cc-chip-new-emails" />
          <Chip icon={Users} color="#F59E0B" label="Decision Makers"
            value={whatChanged?.new_contacts ?? 0}
            testid="smith-cc-chip-new-contacts" />
          <Chip icon={PhoneCall} color="#22C55E" label="Contacts Made"
            value={whatChanged?.contacted ?? 0}
            testid="smith-cc-chip-contacted" />
          <Chip icon={Mail} color="#EAB308" label="Replies"
            value={whatChanged?.replies ?? 0}
            onClick={() => setFilter("replies")}
            testid="smith-cc-chip-replies" />
          <Chip icon={Bell} color="#EF4444" label="Follow-ups Due"
            value={whatChanged?.followups_due ?? 0}
            onClick={() => setFilter("followups_due")}
            testid="smith-cc-chip-followups" />
        </div>
      </section>

      {/* ── Action Center ────────────────────────────────────────── */}
      <section data-testid="smith-cc-action-center">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-3.5 h-3.5 text-amber-300" />
          <h3 className="text-[11px] uppercase tracking-widest text-slate-300 font-bold">
            Action Center — o que fazer agora
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
          {ACTION_TILES.map((t) => {
            const Icon = t.icon;
            const count = tiles?.[t.id]?.count || 0;
            const active = filter === t.id;
            return (
              <button key={t.id} type="button"
                onClick={() => setFilter(t.id)}
                data-testid={`smith-cc-tile-${t.id}`}
                className={`text-left rounded-xl border p-3 transition-colors ${active
                  ? "border-white/40 bg-white/5"
                  : "border-white/10 hover:border-white/25"}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                       style={{ background: `${t.color}22`, color: t.color }}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold">
                    {t.sub}
                  </span>
                </div>
                <div className="text-[22px] font-extrabold tabular-nums text-white"
                     data-testid={`smith-cc-tile-${t.id}-count`}>
                  {count}
                </div>
                <div className="text-[11px] text-slate-400 font-bold">{t.label}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Saved Views bar (Iter215) ─────────────────────────────── */}
      <section data-testid="smith-cc-saved-views"
        className="rounded-2xl border border-white/10 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Bookmark className="w-3.5 h-3.5 text-purple-300" />
            <h3 className="text-[11px] uppercase tracking-widest text-slate-300 font-bold">
              Saved Views — abra o dia onde você parou
            </h3>
          </div>
          <SaveViewButton onSave={saveCurrentView} disabled={savingView} />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="smith-cc-views-list">
          {savedViews.length === 0 && (
            <span className="text-[11px] text-slate-500 italic">
              carregando views padrão…
            </span>
          )}
          {savedViews.map((v) => {
            const active = activeViewId === v.id;
            return (
              <div key={v.id} className="flex items-center">
                <button type="button" onClick={() => applyView(v)}
                  data-testid={`smith-cc-view-${v.id}`}
                  className={`h-7 px-2.5 rounded-l text-[11px] font-bold inline-flex items-center gap-1 border transition-colors ${active
                    ? "border-purple-500 bg-purple-500/15 text-white"
                    : "border-white/15 text-slate-300 hover:border-purple-500/40"}`}>
                  <span>{v.icon}</span>
                  <span>{v.name}</span>
                </button>
                {!v.is_default && (
                  <button type="button" onClick={() => deleteView(v)}
                    data-testid={`smith-cc-view-delete-${v.id}`}
                    title="Remover view"
                    className={`h-7 w-6 rounded-r border-y border-r text-slate-500 hover:text-rose-300 inline-flex items-center justify-center ${active
                      ? "border-purple-500 bg-purple-500/15" : "border-white/15"}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Priority Leads Table (10 columns) ────────────────────── */}
      <section data-testid="smith-cc-priority-table">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <h3 className="text-[11px] uppercase tracking-widest text-slate-300 font-bold">
              {filter === "new" ? "New Leads (últimos 7 dias)"
                : filter === "priority" ? "Priority Leads"
                : filter === "contact_today" ? "Contact Today"
                : filter === "followups_due" ? "Follow-ups Due"
                : filter === "replies" ? "Replies"
                : "Stale Leads"}
              <span className="ml-2 text-slate-500 font-normal">
                · {visibleLeads.length}
                {hiddenCount > 0 && (
                  <span className="text-slate-600"> / {leads.length}</span>
                )} leads
              </span>
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Iter233 · email-only toggle */}
            <button type="button"
              onClick={() => setOnlyWithEmail((v) => !v)}
              data-testid="smith-cc-toggle-only-email"
              title="Hide leads without a valid contact email"
              className={`h-7 px-2.5 rounded text-[10.5px] font-bold inline-flex items-center gap-1 border transition-colors ${onlyWithEmail
                ? "border-emerald-500 bg-emerald-500/15 text-emerald-100"
                : "border-white/15 text-slate-300 hover:border-emerald-500/40"}`}>
              <Mail className="w-3 h-3" />
              {onlyWithEmail ? "Só com e-mail" : "Todos"}
            </button>
            {/* Iter233 · bulk delete */}
            {selectedIds.size > 0 && (
              <button type="button"
                onClick={bulkDeleteSelected}
                disabled={bulkDeleting}
                data-testid="smith-cc-bulk-delete"
                className="h-7 px-2.5 rounded text-[10.5px] font-bold text-white inline-flex items-center gap-1 disabled:opacity-50"
                style={{ background: "linear-gradient(135deg,#EF4444 0%,#7F1D1D 100%)" }}>
                {bulkDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                Excluir ({selectedIds.size})
              </button>
            )}
            <button type="button" onClick={toggleNBA}
              disabled={nbaLoading}
              data-testid="smith-cc-nba-toggle"
              title="Sugestões AI de Claire para cada lead"
              className={`h-7 px-2.5 rounded text-[10.5px] font-bold inline-flex items-center gap-1 border transition-colors ${nbaShown
                ? "border-purple-500 bg-purple-500/15 text-purple-100"
                : "border-white/15 text-slate-300 hover:border-purple-500/40"}`}>
              {nbaLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
              AI Next Best Action
            </button>
            <div className="flex items-center gap-1">
              {ACTION_TILES.map((t) => (
                <button key={t.id} type="button" onClick={() => setFilter(t.id)}
                  className={`h-6 px-2 rounded text-[10px] font-bold ${filter === t.id
                    ? "bg-white/15 text-white" : "text-slate-400 hover:text-white"}`}>
                  {t.label}
                </button>
              ))}
              <button type="button" onClick={() => setFilter("new")}
                className={`h-6 px-2 rounded text-[10px] font-bold ${filter === "new"
                  ? "bg-white/15 text-white" : "text-slate-400 hover:text-white"}`}>
                🆕 New
              </button>
            </div>
          </div>
        </div>

        {/* Iter233 · banner explaining hidden count */}
        {hiddenCount > 0 && (
          <div className="mb-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-[11.5px] text-emerald-100 flex items-center justify-between gap-3"
               data-testid="smith-cc-hidden-banner">
            <span>
              <b>{hiddenCount}</b> lead(s) ocultos — sem e-mail válido ou duplicados por e-mail.
            </span>
            <button type="button"
              onClick={() => setOnlyWithEmail(false)}
              data-testid="smith-cc-show-hidden"
              className="text-[10.5px] font-bold underline hover:text-white">
              Mostrar todos
            </button>
          </div>
        )}

        <div className="rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]"
                   data-testid="smith-cc-leads-table">
              <thead className="bg-white/5 text-slate-400 uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="text-left px-3 py-2 font-bold w-8">
                    <input type="checkbox"
                      data-testid="smith-cc-select-all"
                      checked={visibleLeads.length > 0 && selectedIds.size === visibleLeads.length}
                      onChange={toggleSelectAll}
                      title="Selecionar todos"
                      className="cursor-pointer accent-rose-500" />
                  </th>
                  <th className="text-left px-3 py-2 font-bold">Company</th>
                  <th className="text-left px-2 py-2 font-bold">Priority</th>
                  <th className="text-right px-2 py-2 font-bold">Score</th>
                  <th className="text-left px-2 py-2 font-bold">Worked</th>
                  <th className="text-left px-2 py-2 font-bold">Last Contact</th>
                  <th className="text-left px-2 py-2 font-bold">Next Action</th>
                  <th className="text-left px-2 py-2 font-bold">Owner</th>
                  <th className="text-left px-2 py-2 font-bold">Updated</th>
                  <th className="text-right px-3 py-2 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {leadsLoading && (
                  <tr><td colSpan={10} className="p-6 text-center text-slate-500">
                    <Loader2 className="w-4 h-4 inline-block animate-spin mr-1" /> Loading…
                  </td></tr>
                )}
                {!leadsLoading && visibleLeads.length === 0 && (
                  <tr><td colSpan={10} className="p-6 text-center text-slate-500">
                    Nada aqui — troque o filtro ou clique <b>Refresh Data</b>.
                  </td></tr>
                )}
                {visibleLeads.map((L) => {
                  const w = WORKED_STYLE[L.worked_status] || WORKED_STYLE.NOT_WORKED;
                  const isSelected = selectedIds.has(L.id);
                  return (
                    <tr key={L.id} data-testid={`smith-cc-lead-row-${L.id}`}
                        className={`hover:bg-white/5 ${isSelected ? "bg-rose-500/5" : ""}`}>
                      <td className="px-3 py-2 w-8">
                        <input type="checkbox"
                          data-testid={`smith-cc-lead-select-${L.id}`}
                          checked={isSelected}
                          onChange={(e) => toggleSelect(L.id, e)}
                          onClick={(e) => e.stopPropagation()}
                          className="cursor-pointer accent-rose-500" />
                      </td>
                      <td className="px-3 py-2 min-w-[220px]">
                        <div className="flex items-center gap-2">
                          <Link to={`/smith/prospects/${L.id}`}
                            data-testid={`smith-cc-lead-name-${L.id}`}
                            className="font-bold text-white hover:text-sky-300 truncate">
                            {L.company_name}
                          </Link>
                          {L.is_new && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 font-bold"
                                  data-testid={`smith-cc-lead-new-${L.id}`}>
                              🆕 NEW
                            </span>
                          )}
                        </div>
                        <div className="text-[10.5px] text-slate-500 truncate">
                          {[L.city, L.state, L.segment].filter(Boolean).join(" · ")}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <span className="text-[11px] font-bold"
                              style={{ color: L.priority.min >= 90 ? "#EF4444"
                                : L.priority.min >= 75 ? "#F97316"
                                : L.priority.min >= 55 ? "#EAB308" : "#94A3B8" }}>
                          {L.priority.icon} {L.priority.label}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right font-extrabold tabular-nums text-white">
                        {L.opportunity_score}
                      </td>
                      <td className="px-2 py-2">
                        <span className="text-[11px] font-bold inline-flex items-center gap-1"
                              style={{ color: w.color }}
                              data-testid={`smith-cc-lead-worked-${L.id}`}>
                          {w.dot} {w.label}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-slate-300">
                        {L.last_contact_at ? (
                          <div>
                            <div className="text-[11px] font-bold">
                              {(L.last_contact_type || "").replace("_", " ").toLowerCase()}
                            </div>
                            <div className="text-[10px] text-slate-500">{timeAgo(L.last_contact_at)}</div>
                          </div>
                        ) : (<span className="text-slate-500 italic">Never</span>)}
                      </td>
                      <td className="px-2 py-2 text-slate-300 max-w-[260px]">
                        <NextActionCell
                          lead={L} nba={nbaMap[L.id]}
                          nbaShown={nbaShown}
                          onApply={() => applyNBA(L.id)}
                        />
                      </td>
                      <td className="px-2 py-2 text-slate-400 text-[11px]">
                        {L.owner_id}
                      </td>
                      <td className="px-2 py-2 text-slate-400 text-[11px]">
                        {timeAgo(L.updated_at)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Link to={`/smith/outreach?prospect_id=${L.id}&tab=compose`}
                            title="Abrir no Outreach AI · Claire redige já com o contexto do lead"
                            data-testid={`smith-cc-lead-email-${L.id}`}
                            className="w-7 h-7 rounded flex items-center justify-center bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-300">
                            <Send className="w-3 h-3" />
                          </Link>
                          <button type="button"
                            onClick={() => setLogCallProspect({ id: L.id, name: L.company_name })}
                            title="Log call"
                            data-testid={`smith-cc-lead-call-${L.id}`}
                            className="w-7 h-7 rounded flex items-center justify-center bg-sky-500/15 hover:bg-sky-500/30 text-sky-300">
                            <PhoneCall className="w-3 h-3" />
                          </button>
                          <button type="button"
                            onClick={() => markContacted(L.id)}
                            title="Mark contacted (phone)"
                            data-testid={`smith-cc-lead-mark-${L.id}`}
                            className="w-7 h-7 rounded flex items-center justify-center bg-white/10 hover:bg-white/25 text-white">
                            <ShieldCheck className="w-3 h-3" />
                          </button>
                          <Link to={`/smith/prospects/${L.id}`}
                            title="Open dossier"
                            data-testid={`smith-cc-lead-open-${L.id}`}
                            className="w-7 h-7 rounded flex items-center justify-center bg-white/5 hover:bg-white/20 text-slate-300">
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {logCallProspect && (
        <LogCallModal
          prospectId={logCallProspect.id}
          companyName={logCallProspect.name}
          onClose={() => setLogCallProspect(null)}
          onLogged={() => { loadLeads(filter); loadSummary(); }}
        />
      )}
    </div>
  );
}

function Chip({ icon: Icon, color, label, value, onClick, testid }) {
  const clickable = !!onClick;
  const inner = (
    <>
      <div className="w-5 h-5 rounded flex items-center justify-center"
           style={{ background: `${color}22`, color }}>
        <Icon className="w-3 h-3" />
      </div>
      <span className="text-[11px] text-slate-300">{label}</span>
      <span className="text-[13px] font-extrabold tabular-nums"
            style={{ color }}>{value}</span>
      {clickable && <ChevronRight className="w-3 h-3 text-slate-500" />}
    </>
  );
  return clickable ? (
    <button type="button" onClick={onClick}
      data-testid={testid}
      className="h-8 px-2.5 rounded-lg border border-white/10 hover:border-white/30 inline-flex items-center gap-1.5">
      {inner}
    </button>
  ) : (
    <div className="h-8 px-2.5 rounded-lg border border-white/10 inline-flex items-center gap-1.5"
         data-testid={testid}>
      {inner}
    </div>
  );
}


// ── Iter215 · Next Action cell (with AI recommendation) ────────────────

function NextActionCell({ lead, nba, nbaShown, onApply }) {
  const nextAction = lead.next_action;
  const hasNba = nba && !nba.error && nba.action;
  // Priority: show existing next_action if set; otherwise Claire's suggestion
  // when the AI toggle is on; otherwise a dash.
  if (nextAction) {
    return (
      <div className="truncate max-w-[240px]" title={nextAction}>
        <span className="text-[11.5px]">{nextAction}</span>
      </div>
    );
  }
  if (!nbaShown) {
    return <span className="text-slate-500 italic text-[11px]">—</span>;
  }
  if (!nba) {
    return (
      <span className="inline-flex items-center gap-1 text-[10.5px] text-slate-500">
        <Loader2 className="w-3 h-3 animate-spin" /> Claire pensando…
      </span>
    );
  }
  if (nba.error) {
    return <span className="text-rose-400 text-[10.5px]">falha</span>;
  }
  const badge = nba.source === "claire"
    ? { label: "Claire", color: "#A78BFA", bg: "rgba(167,139,250,0.15)" }
    : { label: "Rules",  color: "#38BDF8", bg: "rgba(56,189,248,0.12)" };

  return (
    <div className="space-y-1" data-testid={`smith-cc-nba-${lead.id}`}>
      <div className="flex items-start gap-1.5">
        <span className="text-[9px] px-1 py-0.5 rounded font-bold shrink-0"
              style={{ color: badge.color, background: badge.bg }}>
          🤖 {badge.label}
        </span>
        <span className="text-[11px] text-slate-100 line-clamp-2 leading-snug"
              title={nba.action}>
          {nba.action}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <button type="button" onClick={onApply}
          data-testid={`smith-cc-nba-apply-${lead.id}`}
          className="h-5 px-1.5 rounded text-[9.5px] font-bold text-white inline-flex items-center gap-0.5"
          style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#6D28D9 100%)" }}>
          <Check className="w-2.5 h-2.5" /> Set as Next
        </button>
        {nba.cta_label && (
          <span className="text-[9.5px] text-slate-400">· {nba.cta_type}</span>
        )}
        {typeof nba.confidence === "number" && (
          <span className="text-[9.5px] text-slate-500 tabular-nums">
            · {nba.confidence}%
          </span>
        )}
      </div>
    </div>
  );
}


// ── Iter215 · SaveView button + inline modal ──────────────────────────

function SaveViewButton({ onSave, disabled }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const commit = async () => {
    if (!name.trim()) return;
    await onSave(name);
    setName(""); setOpen(false);
  };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} disabled={disabled}
        data-testid="smith-cc-saveview-open"
        className="h-7 px-2.5 rounded text-[10.5px] font-bold text-white inline-flex items-center gap-1 disabled:opacity-50"
        style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#6D28D9 100%)" }}>
        <BookmarkPlus className="w-3 h-3" /> Save View
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1"
         data-testid="smith-cc-saveview-inline">
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && commit()}
        placeholder="Nome da view…"
        data-testid="smith-cc-saveview-input"
        className="h-7 px-2 rounded border border-white/20 bg-black/30 text-[11px] text-white placeholder:text-slate-500 focus:border-purple-500/60 focus:outline-none w-[160px]" />
      <button type="button" onClick={commit} disabled={disabled || !name.trim()}
        data-testid="smith-cc-saveview-commit"
        className="h-7 px-2 rounded text-[10.5px] font-bold text-white inline-flex items-center gap-1 disabled:opacity-50"
        style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#6D28D9 100%)" }}>
        <Check className="w-3 h-3" /> Save
      </button>
      <button type="button" onClick={() => { setOpen(false); setName(""); }}
        data-testid="smith-cc-saveview-cancel"
        className="h-7 w-7 rounded border border-white/15 text-slate-400 hover:text-white flex items-center justify-center">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
